import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query";
import { toast } from "react-toastify";
import {
  promptForCsrfRefresh,
  promptForSessionExpired,
} from "../../Builder/Components/ConfirmDialogContext";

const el: any = document.querySelector("#app");
export const { contactsClientId, apiBase } = JSON.parse(
  el?.dataset?.page ?? ""
).props;

const isCsrfTokenMismatch = (error: FetchBaseQueryError): boolean => {
  const { status, data } = error;
  const message =
    typeof data === "object" && data !== null
      ? String((data as { message?: unknown }).message ?? "").trim()
      : "";

  return (
    status === 419 ||
    (status === 409 && message.toLowerCase() === "csrf token mismatch.")
  );
};

export const baseQueryWithErrorHandling = ({
  baseUrl,
  paramsSerializer,
  prepareHeaders,
}: any = {}): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    paramsSerializer,
    prepareHeaders,
    redirect: "manual", // Important to prevent automatic following of redirects
    validateStatus: (response) => {
      if (response.status >= 200 && response.status < 300) {
        return true;
      }
      if (response.status === 403) {
        toast.error(
          "You don't have enough permissions to perform this action."
        );
      }
      return false;
    },
  });

  return async (args, api, extraOptions) => {
    const hideToasts = typeof args === "object" && args !== null && "hideToasts" in args
      ? Boolean((args as any).hideToasts)
      : false;
    const result = await baseQuery(args, api, extraOptions);

    // Handle CSRF, unauthorized, and parsing errors (HTML responses from redirects)
    if (result.error) {
      const status = result.error.status;

      if (isCsrfTokenMismatch(result.error)) {
        promptForCsrfRefresh();
        return result;
      }

      // PARSING_ERROR typically means the server returned HTML instead of JSON
      // This happens when Laravel redirects unauthenticated users to login
      if (status === "PARSING_ERROR") {
        promptForSessionExpired();
        return null as any;
      }

      // Check if status is 401 (must be a number)
      if (typeof status === "number" && status === 401) {
        console.log("Detected 401 unauthorized");
        // Check if it's not a token_expired error (which is handled by baseProxyQueryWithReauth)
        if (!(result.error.data as any)?.token_expired) {
          promptForSessionExpired();
        }
      }
    }

    return result;
  };
};

// Define base query with authorization header
export const baseProxyQuery = (prefix: string) =>
  baseQueryWithErrorHandling({
    baseUrl: `${apiBase}/${prefix ? `${prefix}/` : ""}`,
    paramsSerializer,
    prepareHeaders: (headers: any, { getState }: any) => {
      const token = (window as any).apiToken;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  });

export const baseProxyQueryWithReauth =
  (prefix: "logic/workflows/api" | "contacts/v1" | "logic/reporting" | "") =>
  async (args: any, store: any, extraOptions: any) => {
    const hideToasts = typeof args === "object" && args !== null && "hideToasts" in args
      ? Boolean(args.hideToasts)
      : false;
    let result = await baseProxyQuery(prefix)(args, store, extraOptions);

    if (result.error && result.error.status === 401) {
      // Try to refresh the token
      if ((result.error.data as any).token_expired) {
        const newTokenResponse = await fetch(`/api-gateway/get-new-token`, {
          headers: { Accept: "application/json" },
        });
        // PHP session expired - prompt the user to log in again.
        if (newTokenResponse.status === 401) {
          promptForSessionExpired();
          result = null as any;
        } else {
          const newTokenData = await newTokenResponse.json();
          (window as any).apiToken = newTokenData.token;

          // Retry the original request
          result = await baseProxyQuery(prefix)(args, store, extraOptions);
        }
      }
    }
    if (result.error) {
      if (!hideToasts && result.error.data) {
        toast.error(
          `Error: (${result.error.status}) ${
            (result.error.data as any).error ||
            (result.error.data as any).message ||
            "An error occurred."
          }`,
          { toastId: "error-message" }
        );
      }
    }
    return result;
  };

// Custom paramsSerializer function for query parameter serialization
const paramsSerializer = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();

  for (const key in params) {
    const value = params[key];

    if (Array.isArray(value)) {
      // Serialize arrays with the '[]' notation
      value.forEach((item) => {
        searchParams.append(`${key}`, item);
      });
    } else {
      // Serialize other values
      if (!!value) {
        searchParams.append(key, value);
      }
    }
  }

  return searchParams.toString();
};
